import { format, formatDistanceToNow } from "date-fns";
import type { DuviSharedAdminActiveSession } from "@duvi-agency/auth";
import { me, type SessionInfo } from "../../api/client";
import { duviCoreAdminDescribeUserAgent } from "../../utils/duvi-core-admin-describe-user-agent";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";

function safe(fn: () => string, fallback: string): string {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function toActiveSession(s: SessionInfo): DuviSharedAdminActiveSession {
  return {
    id: s.session_id,
    ip: s.ip_address,
    // The stored value is a raw user agent. The row exists so someone can
    // recognise their own laptop and spot one that is not theirs, which a
    // 120-character Mozilla string does not let them do.
    device: duviCoreAdminDescribeUserAgent(s.user_agent),
    signed: safe(
      () => formatDistanceToNow(new Date(s.created_at), { addSuffix: true }),
      s.created_at,
    ),
    expires: safe(() => format(new Date(s.expires_at * 1000), "PPp"), ""),
    current: s.current,
  };
}

export function useDuviCoreAdminSettingsPage() {
  const { data: profile, refetch: refetchProfile } = useDuviCoreAdminQuery(
    () => me.get(),
    [],
  );
  const { data: sessionData, refetch } = useDuviCoreAdminQuery(
    () => me.listSessions(),
    [],
  );

  const sessions: DuviSharedAdminActiveSession[] = (sessionData ?? []).map(
    toActiveSession,
  );

  const onLogoutOthers = () => {
    me.logoutAllSessions()
      .then(() => refetch())
      .catch(() => {});
  };

  // The display name and the profile photo belong to the core API, not to the
  // auth service, so the shared settings surface takes them as handlers rather
  // than calling for them. Each refetches the profile afterwards so the shell
  // (avatar + user menu) agrees with the panel.
  const onSaveName = async (name: string) => {
    await me.updateName(name);
    refetchProfile();
  };

  const onUploadAvatar = async (file: File) => {
    const env = await me.uploadAvatar(file);
    refetchProfile();
    return env.data.avatar_url;
  };

  const onRemoveAvatar = async () => {
    await me.removeAvatar();
    refetchProfile();
  };

  return {
    name: profile?.name,
    email: profile?.email,
    avatarUrl: profile?.avatar_url,
    sessions,
    onLogoutOthers,
    onSaveName,
    onUploadAvatar,
    onRemoveAvatar,
  };
}
